import { useCallback, useEffect } from 'react';
import { useRemoveElements } from '@client/store/project';
import {
  getSelectedElement,
  getSelectedElementId,
  getUIState,
  useUIState,
} from '@client/store/ui';
import { URL_CATEGORY_NONE } from '@client/models/router.model';
import { isProductElement } from '@shared/helpers/recursive-element.helpers';
import {
  useCopyElementToClipboard,
  usePasteElementFromClipboard,
} from './use-copy-paste.hooks';
import {
  setUndoRedoHotkeysActive,
  useRedo,
  useUndo,
} from './use-undo-redo.hooks';
import { useSnackbarWithUndo } from './use-undo-snackbar.hooks';
import { useSelectedProposalId } from './use-selected-proposal-id.hooks';
import type { IElementID } from '@shared/models/project.model';
import {
  consumePendingRestoreElementId,
  setPendingRestoreElementId,
} from './use-undo-element-restore';
import { useKeyboardEventCallback } from './use-events.hooks';
import { expressionPanel } from '@features/expressions/ExpressionInputPanel/ExpressionInputPanel';
import {
  useIsProjectList,
  useProjectNavigation,
} from '@client/hooks/use-router.hooks';
import { Page } from '@client/store/ui/ui.model';
import { KeyboardKeys } from '@shared/models/keyboard.model';
import { is } from '@client/helpers/dom.guards';
import { guards } from '@shared/helpers/typeguards.helpers';
import { isOneOf } from '@shared/helpers/array.helpers';

const GLOBAL_KEY_LISTENER_PAGES = [Page.Elements, Page.Diff, Page.Products];

export const UNDO_REDO_PAGES = [
  Page.Elements,
  Page.Diff,
  Page.Products,
  Page.Design,
  Page.Import,
];

/**
 * Registers document-level shortcuts while a project is open (not on the
 * project list). Escape clears URL-backed element selection on Elements/Diff/Products;
 * copy/paste and delete stay limited to those pages. Undo/redo attach on
 * {@link UNDO_REDO_PAGES} (includes Design and Import).
 */
export const useGlobalKeyListener = (): void => {
  const enqueueSnackbarWithUndo = useSnackbarWithUndo();
  const undo = useUndo();
  const redo = useRedo();
  const selectedProposalId = useSelectedProposalId();
  const navigateTo = useProjectNavigation();

  const removeElements = useRemoveElements({
    onDeleted: useCallback(
      (name: string, elementId: IElementID) => {
        setPendingRestoreElementId(elementId);
        const message = selectedProposalId
          ? `${name} removed`
          : `${name} deleted`;
        enqueueSnackbarWithUndo(message, {
          withUndo: true,
          autoHideDuration: 8000,
          beforeUndo: () => consumePendingRestoreElementId(),
          afterUndo: (idToRestore) => {
            if (guards.is.string(idToRestore) && idToRestore)
              navigateTo({ elementId: idToRestore });
          },
        });
      },
      [selectedProposalId, enqueueSnackbarWithUndo, navigateTo],
    ),
  });
  const copyElementToClipboard = useCopyElementToClipboard();
  const pasteElement = usePasteElementFromClipboard();

  const removeElement = useCallback(
    (id: string) => removeElements(id),
    [removeElements],
  );

  const handleCopyPaste = useCallback(
    (cmd: boolean, ctrl: boolean, key: string) => {
      if (key === 'c' && (cmd || ctrl)) {
        copyElementToClipboard();
      }

      if (key === 'v' && (cmd || ctrl)) {
        pasteElement();
      }
    },
    [copyElementToClipboard, pasteElement],
  );

  const handleEditKeyboardEvent = useKeyboardEventCallback(
    (event: KeyboardEvent) => {
      const cmd = event.getModifierState('Meta');
      const ctrl = event.getModifierState('Control');
      const key = event.key;
      const page = getUIState().selectedPage;
      const target = event.target;
      const isInput = is.Input(target) || is.TextArea(target);

      // Deselect element on Escape key
      if (
        key === KeyboardKeys.ESCAPE &&
        isOneOf(GLOBAL_KEY_LISTENER_PAGES, page) &&
        !isInput
      ) {
        // Match background-click deselect: clear URL-backed element selection only.
        const selectedElementId = getSelectedElementId();
        if (selectedElementId && selectedElementId !== URL_CATEGORY_NONE) {
          event.preventDefault();
          void navigateTo({ elementId: URL_CATEGORY_NONE });
        }
        return;
      }

      if (key === KeyboardKeys.BACKSPACE || key === KeyboardKeys.DELETE) {
        const selectedElement = getSelectedElement();
        if (selectedElement && !isProductElement(selectedElement)) {
          event.preventDefault();
          removeElement(selectedElement.id);
        }
        return;
      }

      handleCopyPaste(cmd, ctrl, key);
    },

    // Make sure these are not triggered when the user is typing in input or selecting stuff in modal
    { ignoreInputEvents: true, ignoreOverlayEvents: true },
  );

  const handleUndoRedoKeyboardEvent = useKeyboardEventCallback(
    (event: KeyboardEvent) => {
      const cmd = event.getModifierState('Meta');
      const ctrl = event.getModifierState('Control');
      const shift = event.getModifierState('Shift');
      const key = event.key;

      if (
        (key === 'y' && (cmd || ctrl)) ||
        (key === 'z' && (cmd || ctrl) && shift)
      ) {
        event.preventDefault();
        event.stopPropagation();
        redo();
        return;
      }

      if (key === 'z' && (cmd || ctrl)) {
        event.preventDefault();
        event.stopPropagation();
        const idToRestore = consumePendingRestoreElementId();
        undo().then(() => {
          if (idToRestore) navigateTo({ elementId: idToRestore });
        });
      }
    },
    // stopPropagation defaults to true in eventHandler, which would swallow
    // every keydown on UNDO_REDO_PAGES (incl. Design) before it bubbles to the
    // map's window-level key streams. Only stop propagation when we actually
    // handle undo/redo (done explicitly above).
    {
      ignoreInputEvents: true,
      ignoreOverlayEvents: true,
      ignoreOnlyTextInputForKeyboard: true,
      stopPropagation: false,
    },
  );

  const expressionPanelOpen = expressionPanel.useIsOpen();
  const { selectedPage } = useUIState('selectedPage');
  const isProjectList = useIsProjectList();

  const disableEditKeyListener =
    isProjectList ||
    expressionPanelOpen ||
    !isOneOf(GLOBAL_KEY_LISTENER_PAGES, selectedPage);

  const disableUndoRedoKeyListener =
    isProjectList ||
    expressionPanelOpen ||
    !isOneOf(UNDO_REDO_PAGES, selectedPage);

  useEffect(() => {
    if (disableEditKeyListener) {
      return;
    }

    window.document.addEventListener('keydown', handleEditKeyboardEvent);
    return () => {
      window.document.removeEventListener('keydown', handleEditKeyboardEvent);
    };
  }, [handleEditKeyboardEvent, disableEditKeyListener]);

  useEffect(() => {
    if (disableUndoRedoKeyListener) {
      setUndoRedoHotkeysActive(false);
      return;
    }

    setUndoRedoHotkeysActive(true);
    window.document.addEventListener('keydown', handleUndoRedoKeyboardEvent);
    return () => {
      setUndoRedoHotkeysActive(false);
      window.document.removeEventListener(
        'keydown',
        handleUndoRedoKeyboardEvent,
      );
    };
  }, [handleUndoRedoKeyboardEvent, disableUndoRedoKeyListener]);
};
